import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, AtSign, Mail, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { useTestFlight } from "@/context/TestFlightContext";
import { joinWaitlist } from "@/lib/api";

// Compact email capture — the hero stays clean (one CTA + store badges), so
// the "stay updated" form lives here in the footer.
function FooterSubscribe() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const clean = email.trim();
    if (!clean) return;
    setSending(true);
    try {
      await joinWaitlist({ email: clean, source: "footer" });
      setSent(true);
      toast.success("You're on the list.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && /already/i.test(detail)) {
        setSent(true);
        toast.success("You're already on the list.");
      } else {
        toast.error("Something went wrong — try again.");
      }
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <p className="mt-6 inline-flex items-center gap-2 text-sm text-neon-cyan" data-testid="footer-subscribe-sent">
        <Check className="w-4 h-4" /> You're in — we'll be in touch.
      </p>
    );
  }

  return (
    <div className="mt-6 max-w-xs">
      <p className="text-sm font-semibold text-white" data-testid="footer-subscribe-heading">
        Want partner venues in your city? Tell us.
      </p>
      <form onSubmit={submit} className="mt-3 flex items-stretch gap-2" data-testid="footer-subscribe">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email for city updates"
        aria-label="Email for city updates"
        data-testid="footer-subscribe-input"
        className="h-10 flex-1 min-w-0 rounded-full bg-white/5 border border-white/10 px-4 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-neon-cyan/40"
      />
      <button
        type="submit"
        disabled={sending}
        data-testid="footer-subscribe-button"
        className="h-10 shrink-0 rounded-full bg-white text-black text-sm font-semibold px-4 hover:bg-white/90 transition-colors disabled:opacity-60"
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Subscribe"}
      </button>
    </form>
    </div>
  );
}

export default function Footer() {
  const { openTestFlight } = useTestFlight();
  const year = new Date().getFullYear();
  // Track the out-of-DFW banner so we can shift the watermark up when it's
  // visible — otherwise the banner's bottom-right pill overlaps the last
  // letters of "GateMate" for visitors outside the metro.
  const [dfwBannerVisible, setDfwBannerVisible] = useState(false);
  useEffect(() => {
    if (typeof document === "undefined") return;
    const check = () =>
      setDfwBannerVisible(!!document.querySelector('[data-testid="dfw-location-banner"]'));
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  return (
    <footer
      className="relative overflow-hidden border-t border-white/5 bg-[#060d0a] pt-20"
      data-testid="footer"
    >
      <div className="glow-orb w-[420px] h-[420px] bg-neon-violet/20 -bottom-40 left-1/4" />
      <div className="container-x relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 pb-16">
          <div className="col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <img src="/brand/logo-256.png" alt="GateMate logo" width="36" height="36" className="w-9 h-9 rounded-xl" />
              <span className="font-display text-xl font-bold">GateMate</span>
            </div>
            <p className="text-zinc-400 max-w-xs text-sm leading-relaxed">
              GateMate is an online platform for real-time networking at live events — connecting people physically present at the same venue — with event scheduling, attendee coordination and engagement tracking for hosts.
            </p>
            <div className="flex items-center gap-3 mt-6">
              {[
                { Icon: Instagram, label: "instagram", href: "https://instagram.com/gatemate_" },
                { Icon: Twitter, label: "twitter", href: "https://twitter.com/GateMate_" },
                { Icon: AtSign, label: "threads", href: "https://threads.net/@gatemate_" },
                { Icon: Mail, label: "email", href: "mailto:support@gatemate.net" },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${label}`}
                  aria-label={label}
                  className="grid place-items-center w-10 h-10 rounded-full glass hover:glass-cyan transition-all text-zinc-300 hover:text-white"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            <a
              href="mailto:support@gatemate.net"
              data-testid="footer-email-link"
              className="mt-4 inline-block text-sm text-zinc-400 hover:text-neon-cyan transition-colors"
            >
              support@gatemate.net
            </a>
            <FooterSubscribe />
          </div>

          <div>
            <p className="overline mb-4">Explore</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/how-it-works" data-testid="footer-how-it-works" className="text-zinc-400 hover:text-white transition-colors">How it works</Link></li>
              <li><Link to="/pricing" data-testid="footer-pricing" className="text-zinc-400 hover:text-white transition-colors">Pricing</Link></li>
              <li><Link to="/venues" data-testid="footer-for-venues" className="text-zinc-400 hover:text-white transition-colors">For venues</Link></li>
              <li><Link to="/events" data-testid="footer-for-events" className="text-zinc-400 hover:text-white transition-colors">For events</Link></li>
              <li><Link to="/venues/directory" data-testid="footer-venue-directory" className="text-zinc-400 hover:text-white transition-colors">Venue directory</Link></li>
              <li><Link to="/travel" data-testid="footer-travel" className="text-zinc-400 hover:text-white transition-colors">For travelers</Link></li>
              <li><Link to="/about" data-testid="footer-about" className="text-zinc-400 hover:text-white transition-colors">About the founder</Link></li>
              <li><Link to="/blog" data-testid="footer-blog" className="text-zinc-400 hover:text-white transition-colors">Blog</Link></li>
            </ul>
          </div>

          <div>
            <p className="overline mb-4">Get involved</p>
            <ul className="space-y-3 text-sm">
              <li><button onClick={openTestFlight} data-testid="footer-waitlist" className="text-zinc-400 hover:text-white transition-colors">Get GateMate</button></li>
              <li><Link to="/insiders" data-testid="footer-insiders" className="text-zinc-400 hover:text-white transition-colors">Become an Insider</Link></li>
              <li><Link to="/bring-gatemate" data-testid="footer-bring" className="text-zinc-400 hover:text-white transition-colors">Bring GateMate to an event</Link></li>
              <li><a href="https://app.gatemate.net/sign-in" data-testid="footer-workspace" className="text-zinc-400 hover:text-white transition-colors">Sign in</a></li>
              <li><Link to="/events" data-testid="footer-events-business" className="text-zinc-400 hover:text-white transition-colors">For events</Link></li>
              <li><Link to="/investors" data-testid="footer-investors" className="text-zinc-400 hover:text-white transition-colors">For investors</Link></li>
              <li><Link to="/contact" data-testid="footer-contact" className="text-zinc-400 hover:text-white transition-colors">Contact us</Link></li>
              <li><Link to="/press" data-testid="footer-press" className="text-zinc-400 hover:text-white transition-colors">Press kit</Link></li>
              <li><a href="https://app.gatemate.net/sign-in" data-testid="footer-venue-login" className="text-zinc-400 hover:text-white transition-colors">Venue partner login</a></li>
            </ul>
          </div>

          <div>
            <p className="overline mb-4">Legal</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/privacy-policy" data-testid="footer-privacy" className="text-zinc-400 hover:text-white transition-colors">Privacy Policy</Link></li>
              <li>
                <button
                  type="button"
                  data-testid="footer-cookie-preferences"
                  onClick={() => window.dispatchEvent(new Event("gm-open-consent"))}
                  className="text-zinc-400 hover:text-white transition-colors"
                >
                  Cookie preferences
                </button>
              </li>
              <li><Link to="/terms" data-testid="footer-terms" className="text-zinc-400 hover:text-white transition-colors">Terms of Service</Link></li>
              <li><Link to="/community-guidelines" className="text-zinc-400 hover:text-white transition-colors">Community Guidelines</Link></li>
              <li><Link to="/child-safety" data-testid="footer-child-safety" className="text-zinc-400 hover:text-white transition-colors">Child safety</Link></li>
              <li><Link to="/delete-account" data-testid="footer-delete-account" className="text-zinc-400 hover:text-white transition-colors">Delete Account</Link></li>
              <li><a href={`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`} target="_blank" rel="noopener noreferrer" data-testid="footer-sitemap" className="text-zinc-400 hover:text-white transition-colors">Sitemap</a></li>
            </ul>
          </div>
        </div>

        {/* Full-bleed watermark. ``left-1/2 -translate-x-1/2 w-screen`` escapes
            container-x so the giant text always has the entire viewport to
            render into — no more last-letter clipping on ultra-wide screens.
            When the DFW-outside banner is docked bottom-right (~340×124px),
            we fade the 4%-opacity decorative watermark to 0 on md+ so the two
            never collide. Mobile places the banner below the watermark, so no
            fade is needed there. */}
        <div
          className={`relative select-none pointer-events-none overflow-hidden left-1/2 -translate-x-1/2 w-screen transition-opacity duration-300 ${
            dfwBannerVisible ? "md:opacity-0" : "opacity-100"
          }`}
          data-testid="footer-watermark-wrapper"
        >
          <h2
            className="font-display font-bold leading-none text-white/[0.04] text-center tracking-tighter whitespace-nowrap"
            style={{ fontSize: "clamp(4rem, 15vw, 18rem)" }}
          >
            GateMate
          </h2>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 py-8 border-t border-white/5">
          <p className="text-zinc-500 text-sm">© {year} QAlytics LLC. All rights reserved.</p>
          <p
            data-testid="footer-trademark-notice"
            className="text-[11px] text-zinc-600 mt-2 font-mono tracking-tight"
          >
            GateMate® is a registered trademark of QAlytics LLC.
          </p>
          <p className="text-zinc-600 text-xs">Made in Dallas · built for every city's live scene.</p>
        </div>
      </div>
    </footer>
  );
}
